import type { AppRouter } from '@app/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import { KeyRound, Plug } from 'lucide-react'
import { useState } from 'react'
import { useTRPC } from '../lib/trpc'
import { when } from '../lib/when'
import { Choice } from './Choice'
import { Button, Card, Field, FieldSection, TextInput } from './index'
import { useConfirm } from './useConfirm'

type Outputs = inferRouterOutputs<AppRouter>
type Connection = Outputs['auth']['connections'][number]
type SpaceChoice = Outputs['space']['mine'][number]

/**
 * What can act as you, and how to stop it.
 *
 * Agents and tokens share this tab because they answer one question. What
 * separates them is who runs the thing holding the credential, which is what
 * each section says rather than what the tab strip says.
 */
export function Connections({
  spaces,
  onEdit,
}: {
  spaces: SpaceChoice[]
  onEdit: (id: string) => void
}) {
  const trpc = useTRPC()
  const connections = useQuery(trpc.auth.connections.queryOptions())
  const held = connections.data ?? []

  const nameOf = (spaceId: string) =>
    spaces.find((s) => s.spaceId === spaceId)?.name ?? 'a space you have left'

  return (
    <FieldSection
      title="Connected agents"
      description="Tools you have let act as you. Each reaches only what you chose to lend it, and can do only what you can do."
    >
      {held.length === 0 ? (
        <Card tone="sunken">
          <p className="account__empty">
            Nothing is connected. An agent appears here after you authorize it
            from the tool you are connecting.
          </p>
        </Card>
      ) : (
        <div className="account__list">
          {held.map((c) => (
            <Card key={c.id}>
              <div className="account__row">
                <span className="account__icon" aria-hidden>
                  <Plug size={16} />
                </span>
                <div className="account__what">
                  <span className="account__name">{c.clientName}</span>
                  {/*
                   * What it can DO, not only where. A space name says nothing
                   * about whether that agent can message the congregation, and
                   * these lines come from the same function that enforces the
                   * grant rather than from a second description of it.
                   */}
                  {c.access.length === 0 && !c.platformAccess ? (
                    <span className="account__reach">Nothing</span>
                  ) : (
                    <ul className="account__reach-list">
                      {c.access.map((a) => (
                        <li key={a.spaceId} className="account__reach">
                          <span className="account__where">
                            {nameOf(a.spaceId)}
                          </span>
                          <span className="account__level">
                            {a.owned
                              ? 'everything, as its owner'
                              : a.permissions.length === 0
                                ? (a.role ?? 'no permissions')
                                : a.permissions.join(' · ')}
                          </span>
                        </li>
                      ))}
                      {c.platformAccess ? (
                        <li className="account__reach">
                          <span className="account__where">The install</span>
                          <span className="account__level">
                            the tracker and settings
                          </span>
                        </li>
                      ) : null}
                    </ul>
                  )}
                  {/*
                   * The fact that decides whether to end it. A connection
                   * nobody has touched is the one to look at, and a count of
                   * connections cannot say which that is.
                   */}
                  <span className="account__used">
                    {c.lastUsedAt
                      ? `Last used ${when(c.lastUsedAt)}`
                      : 'Never used'}
                  </span>
                </div>
                <Button variant="secondary" onClick={() => onEdit(c.id)}>
                  Change
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </FieldSection>
  )
}

/**
 * Changing what a connection is lent — a view of this sheet, not a second one.
 *
 * Nothing here is a new consent: the owner is in the app where they agreed in
 * the first place, so it mints no code and the connection keeps working at
 * whatever the new reach is, from its next call.
 */
export function ConnectionView({
  connection,
  spaces,
  canLendInstall,
  onBack,
}: {
  connection: Connection
  spaces: SpaceChoice[]
  canLendInstall: boolean
  onBack: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { confirm, dialog } = useConfirm()
  const [chosen, setChosen] = useState<string[]>(connection.spaceIds)
  const [install, setInstall] = useState<string[]>(
    connection.platformAccess ? ['platform'] : [],
  )
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.auth.connections.queryKey(),
    })
  const update = useMutation(
    trpc.auth.updateConnection.mutationOptions({
      onSuccess: () => {
        invalidate()
        onBack()
      },
    }),
  )
  const disconnect = useMutation(
    trpc.auth.disconnect.mutationOptions({
      onSuccess: () => {
        invalidate()
        onBack()
      },
    }),
  )

  const changed =
    chosen.join() !== [...connection.spaceIds].join() ||
    install.length > 0 !== connection.platformAccess

  return (
    <>
      <Choice
        multiple
        label="Spaces"
        options={spaces.map((s) => ({
          value: s.spaceId,
          label: s.name,
          description:
            s.kind === 'personal' ? 'Your own books and drafts.' : undefined,
        }))}
        value={chosen}
        onValueChange={setChosen}
      />
      {canLendInstall ? (
        <Choice
          multiple
          label="The install itself"
          options={[
            {
              value: 'platform',
              label: 'The tracker and install settings',
            },
          ]}
          value={install}
          onValueChange={setInstall}
        />
      ) : null}
      {/* Destructive at the END of the record's own view, never beside the
          commit: it is arrived at by scrolling past every setting, which is
          the right amount of deliberate. */}
      <div className="account__end">
        <p className="account__end-what">
          Disconnecting stops its next call. Nothing it has already done is
          undone.
        </p>
        <Button
          variant="danger"
          onClick={async () => {
            const ok = await confirm({
              title: `Disconnect ${connection.clientName}?`,
              body: `Its next call stops working. Nothing it has already done is undone, and connecting it again means authorizing it from ${connection.clientName} once more.`,
              verb: 'Disconnect',
            })
            if (ok) disconnect.mutate({ grantId: connection.id })
          }}
        >
          Disconnect
        </Button>
      </div>

      <div className="uifoot">
        <span className="uifoot__state">
          {changed ? 'Takes effect on its next call.' : ''}
        </span>
        <span className="uifoot__end">
          <Button variant="secondary" onClick={onBack}>
            Back
          </Button>
          <Button
            disabled={!changed || update.isPending}
            onClick={() =>
              update.mutate({
                grantId: connection.id,
                spaceIds: chosen,
                platformAccess: install.length > 0,
              })
            }
          >
            Save
          </Button>
        </span>
      </div>
      {dialog}
    </>
  )
}

/**
 * API tokens, beside the connections because they answer the same question:
 * what can act as me, and how do I stop it.
 *
 * The difference that shapes this half is that a token is shown ONCE. Only its
 * hash is kept, so the moment of showing it is the only moment it exists —
 * which is why the new token sits in a panel that nothing dismisses on its
 * own, rather than in a toast that a timer takes away while somebody is
 * finding their terminal.
 */
export function Tokens() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { confirm, dialog } = useConfirm()
  const [label, setLabel] = useState('')
  const [minted, setMinted] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const tokens = useQuery(trpc.auth.tokens.queryOptions())
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: trpc.auth.tokens.queryKey() })
  const mint = useMutation(
    trpc.auth.mintToken.mutationOptions({
      onSuccess: (r) => {
        setMinted(r.token)
        setCopied(false)
        setLabel('')
        invalidate()
      },
    }),
  )
  const revoke = useMutation(
    trpc.auth.revokeToken.mutationOptions({ onSuccess: invalidate }),
  )

  const held = tokens.data ?? []

  return (
    <FieldSection
      title="API tokens"
      description="For a client you run yourself. A token carries everything you can do and does not expire, so an agent running in somebody else's tool belongs above instead."
    >
      {minted ? (
        <Card tone="sunken">
          <p className="account__once">
            Copy this now — it is not stored and cannot be shown again.
          </p>
          <code className="account__token">{minted}</code>
          <div className="account__once-actions">
            <Button
              onClick={() => {
                void navigator.clipboard.writeText(minted)
                setCopied(true)
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
            {/* Dismissing is the person's act, never a timer's: the panel is
                the only place this value exists. */}
            <Button variant="quiet" onClick={() => setMinted(null)}>
              Done
            </Button>
          </div>
        </Card>
      ) : null}

      {/* Named at mint time: three tokens called nothing are three tokens
          nobody dares revoke. */}
      <Field label="What it is for">
        <TextInput
          value={label}
          placeholder="The script that posts the luach"
          onChange={(e) => setLabel(e.target.value)}
        />
      </Field>
      <div className="account__act">
        <Button
          disabled={label.trim() === '' || mint.isPending}
          onClick={() => mint.mutate({ label: label.trim() })}
        >
          Create token
        </Button>
      </div>

      {held.length > 0 ? (
        <div className="account__list">
          {held.map((t) => (
            <Card key={t.id}>
              <div className="account__row">
                <span className="account__icon" aria-hidden>
                  <KeyRound size={16} />
                </span>
                <div className="account__what">
                  <span className="account__name">{t.label}</span>
                  <span className="account__used">
                    {t.lastUsedAt
                      ? `Last used ${when(t.lastUsedAt)}`
                      : 'Never used'}
                  </span>
                </div>
                <Button
                  variant="secondary"
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Revoke ${t.label}?`,
                      body: 'Anything still using this token stops working on its next call. A new one can be created, but this value cannot be shown again.',
                      verb: 'Revoke token',
                    })
                    if (ok) revoke.mutate({ tokenId: t.id })
                  }}
                >
                  Revoke
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : null}
      {dialog}
    </FieldSection>
  )
}
